import { GOOGLE_MAPS_API_KEY } from '../config/google-maps-config';

// Utility functions for handling map embed links

/**
 * Validates if a string is a valid Google Maps embed code
 * @param {string} embedCode - The potential map embed code to validate
 * @returns {boolean} - Whether the code is a valid Google Maps embed
 */
export const isValidMapEmbed = (embedCode) => {
  if (!embedCode) return false;
  
  // Check if it's an iframe
  const hasIframe = embedCode.includes('<iframe') && embedCode.includes('</iframe>');
  
  // Check if it's from Google Maps
  const isGoogleMaps = embedCode.includes('google.com/maps');
  
  // Check if it has necessary attributes
  const hasRequiredAttrs = embedCode.includes('src="') && 
                          (embedCode.includes('width="') || embedCode.includes('height="'));
  
  return hasIframe && isGoogleMaps && hasRequiredAttrs;
};

/**
 * Extracts coordinates from a Google Maps embed link.
 * Prefers place coordinates (!8m2!3d!4d or !3d!4d) when present; otherwise uses view center (!2d/!3d),
 * which may not match the actual place (e.g. "The Golden Bull Bar" at 412 14th St is 37.804, -122.27
 * but view center in the embed can be 35.99, -123.03). When only view center is available, consider
 * storing lat/lng from the place page or resolving the place ID via Places API.
 * @param {string} mapEmbedLink - The map embed iframe code or URL
 * @returns {{lat: number|null, lng: number|null}} - The extracted coordinates
 */
export const extractCoordinatesFromMapEmbed = (mapEmbedLink) => {
  if (!mapEmbedLink) return { lat: null, lng: null };
  
  try {
    // Extract the src URL if it's an iframe
    let url = mapEmbedLink;
    const srcMatch = mapEmbedLink.match(/src="([^"]+)"/);
    if (srcMatch && srcMatch[1]) {
      url = decodeURIComponent(srcMatch[1]);
    }
    
    // Also try to decode the URL in case it's encoded
    try {
      url = decodeURIComponent(url);
    } catch (e) {
      // URL was not encoded, continue with original
    }
    
    // 1) Prefer explicit place coordinates: !8m2!3d{lat}!4d{lng} (used when opening place in Maps)
    //    These are the actual place/marker location; view center (!2d/!3d) can be different.
    const placeCoordsMatch = url.match(/!8m2!3d(-?\d+\.?\d+)!4d(-?\d+\.?\d+)/);
    if (placeCoordsMatch) {
      const lat = parseFloat(placeCoordsMatch[1]);
      const lng = parseFloat(placeCoordsMatch[2]);
      if (!isNaN(lat) && !isNaN(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
        return { lat, lng };
      }
    }

    // 2) Place-style pair !3d{lat}!4d{lng} (may appear in some embed/share URLs)
    const placePairMatch = url.match(/!3d(-?\d+\.?\d+)!4d(-?\d+\.?\d+)/);
    if (placePairMatch) {
      const lat = parseFloat(placePairMatch[1]);
      const lng = parseFloat(placePairMatch[2]);
      if (!isNaN(lat) && !isNaN(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
        return { lat, lng };
      }
    }

    // 3) Fallback: !2d (lng) and !3d (lat) — these are the map VIEW center, not always the place.
    //    Use when no !8m2!3d!4d or !3d!4d place coords are present (e.g. many share embeds).
    const lngMatch = url.match(/!2d(-?\d+\.?\d+)/);
    const latMatch = url.match(/!3d(-?\d+\.?\d+)/);
    if (lngMatch && latMatch) {
      const lng = parseFloat(lngMatch[1]);
      const lat = parseFloat(latMatch[1]);
      if (!isNaN(lat) && !isNaN(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
        return { lat, lng };
      }
    }

    // Try other coordinate formats
    const patterns = [
      // Adjacent !2d{lng}!3d{lat} (view center)
      { regex: /!2d(-?\d+\.?\d+)!3d(-?\d+\.?\d+)/, latIndex: 2, lngIndex: 1 },
      // q= parameter: q={lat},{lng}
      { regex: /[?&]q=(-?\d+\.?\d+),(-?\d+\.?\d+)/, latIndex: 1, lngIndex: 2 },
      // @ format in URL: @{lat},{lng}
      { regex: /@(-?\d+\.?\d+),(-?\d+\.?\d+)/, latIndex: 1, lngIndex: 2 },
      // center= parameter
      { regex: /center=(-?\d+\.?\d+),(-?\d+\.?\d+)/, latIndex: 1, lngIndex: 2 },
      // ll= parameter
      { regex: /ll=(-?\d+\.?\d+),(-?\d+\.?\d+)/, latIndex: 1, lngIndex: 2 },
      // sll= parameter
      { regex: /sll=(-?\d+\.?\d+),(-?\d+\.?\d+)/, latIndex: 1, lngIndex: 2 },
      // pb parameter with coordinates: !1d{lat}!2d{lng}
      { regex: /!1d(-?\d+\.?\d+)!2d(-?\d+\.?\d+)/, latIndex: 1, lngIndex: 2 }
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern.regex);
      if (match) {
        const lat = parseFloat(match[pattern.latIndex]);
        const lng = parseFloat(match[pattern.lngIndex]);
        
        // Validate the extracted coordinates
        if (!isNaN(lat) && !isNaN(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
          return { lat, lng };
        }
      }
    }
    
    return { lat: null, lng: null };
  } catch (error) {
    console.error('Error extracting coordinates:', error);
    return { lat: null, lng: null };
  }
};

/**
 * Returns true if the embed URL contains explicit place coordinates (!8m2!3d!4d or !3d!4d),
 * so we don't need to geocode the place name with Mapbox.
 * @param {string} mapEmbedLink - The map embed iframe code or URL
 * @returns {boolean}
 */
export const hasExplicitPlaceCoordinatesInEmbed = (mapEmbedLink) => {
  if (!mapEmbedLink) return false;
  try {
    let url = mapEmbedLink;
    const srcMatch = mapEmbedLink.match(/src="([^"]+)"/);
    if (srcMatch && srcMatch[1]) {
      url = decodeURIComponent(srcMatch[1]);
    }
    return /!8m2!3d(-?\d+\.?\d+)!4d(-?\d+\.?\d+)/.test(url) ||
           /!3d(-?\d+\.?\d+)!4d(-?\d+\.?\d+)/.test(url);
  } catch (e) {
    return false;
  }
};

/**
 * Extracts the place name from the Google Maps embed pb parameter (!2s... segment).
 * Use this with Mapbox Geocoding API to get coordinates when the embed only has view center.
 * @param {string} mapEmbedLink - The map embed iframe code or URL
 * @returns {string} - The place name (e.g. "The Golden Bull Bar") or empty string
 */
export const extractPlaceNameFromMapEmbed = (mapEmbedLink) => {
  if (!mapEmbedLink) return '';
  try {
    let url = mapEmbedLink;
    const srcMatch = mapEmbedLink.match(/src="([^"]+)"/);
    if (srcMatch && srcMatch[1]) {
      url = decodeURIComponent(srcMatch[1]);
    }
    const pbMatch = url.match(/[?&]pb=([^&]+)/);
    const pb = pbMatch ? decodeURIComponent(pbMatch[1]) : url;
    const nameMatch = pb.match(/!2s([^!]+)/);
    if (nameMatch && nameMatch[1]) {
      return decodeURIComponent(nameMatch[1].replace(/\+/g, ' ')).trim();
    }
    return '';
  } catch (e) {
    return '';
  }
};

function decodeEmbedUrl(mapEmbedLink) {
  if (!mapEmbedLink) return '';
  let url = String(mapEmbedLink);
  const srcMatch = url.match(/src=["']([^"']+)["']/);
  if (srcMatch && srcMatch[1]) url = srcMatch[1];
  try {
    url = decodeURIComponent(url);
  } catch (e) {
    // already decoded
  }
  return url;
}

function isOfficialGooglePlaceId(value) {
  if (!value) return false;
  const id = String(value).trim();
  if (/^0x[0-9a-f]+:0x[0-9a-f]+$/i.test(id)) return false;
  return /^(ChIJ|GhIJ|Eh[A-Za-z0-9]|Ek[A-Za-z0-9])/i.test(id);
}

function cidFromFeatureId(featureId) {
  if (!featureId) return null;
  const match = String(featureId).match(/0x[0-9a-f]+:(0x[0-9a-f]+)/i);
  if (!match) return null;
  try {
    return BigInt(match[1]).toString();
  } catch (e) {
    return null;
  }
}

function coordsFromPlace(place, fallbackId) {
  const lat = Number(place?.location?.latitude);
  const lng = Number(place?.location?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const rawId = place?.id || fallbackId || null;
  const googlePlaceId = rawId ? String(rawId).replace(/^places\//, '') : null;
  return { lat, lng, googlePlaceId };
}

function resolveGoogleApiKey(googleApiKeyOrOptions) {
  if (typeof googleApiKeyOrOptions === 'string' && googleApiKeyOrOptions.trim()) {
    return googleApiKeyOrOptions.trim();
  }
  if (googleApiKeyOrOptions && typeof googleApiKeyOrOptions === 'object' && googleApiKeyOrOptions.googleApiKey) {
    return String(googleApiKeyOrOptions.googleApiKey).trim();
  }
  return GOOGLE_MAPS_API_KEY || '';
}

function logGoogleCoords(event, payload = {}) {
  console.log(`[resolveMapEmbed] ${event}`, payload);
}

function isGooglePermissionDenied(data, httpStatus) {
  const status = data?.error?.status || data?.status || '';
  const message = String(data?.error?.message || data?.error_message || '');
  return httpStatus === 403
    || status === 'PERMISSION_DENIED'
    || status === 'REQUEST_DENIED'
    || /blocked/i.test(message)
    || /not authorized/i.test(message);
}

function isGoogleJsPermissionDenied(error) {
  const message = String(error?.message || error || '');
  return /PERMISSION_DENIED/i.test(message)
    || /REQUEST_DENIED/i.test(message)
    || /blocked/i.test(message);
}

/** Skip repeat REST/JS Places (New) calls when blocked for this key. */
let googlePlacesNewBlocked = false;
let googleMapsJsBlocked = false;
let lastGoogleResolveError = null;
let googleMapsJsPromise = null;
let googleMapsJsKey = null;

function resetGoogleResolveSessionErrors() {
  lastGoogleResolveError = null;
}

function noteGoogleResolveError(error) {
  const msg = String(error?.message || error || '');
  if (/timed out/i.test(msg)) {
    // Google never settles calls on a key it rejects (wrong API or website restriction),
    // so a timeout means stop asking for the rest of this session.
    googleMapsJsBlocked = true;
    lastGoogleResolveError = 'Google Maps did not respond. Check that the browser key allows the Maps JavaScript API and this website.';
    return;
  }
  if (/ApiNotActivatedMapError/i.test(msg)) {
    googleMapsJsBlocked = true;
    lastGoogleResolveError = 'Enable Maps JavaScript API in Google Cloud (APIs & Services → Library), then allow it on your browser key.';
    return;
  }
  if (/RefererNotAllowedMapError/i.test(msg)) {
    lastGoogleResolveError = 'This site is not listed in your Google Maps key HTTP referrer restrictions.';
    return;
  }
  if (/InvalidKeyMapError/i.test(msg)) {
    lastGoogleResolveError = 'Google Maps API key is invalid.';
    return;
  }
  if (/Failed to load Google Maps JS/i.test(msg)) {
    lastGoogleResolveError = 'Could not load Google Maps. Check your network and API key.';
    return;
  }
  if (!lastGoogleResolveError && msg && !/PERMISSION_DENIED.*SearchText/i.test(msg)) {
    lastGoogleResolveError = msg;
  }
}

function emptyResolveFields(extra = {}) {
  return {
    latitude: null,
    longitude: null,
    googlePlaceId: extra.googlePlaceId || null,
    googleCid: extra.googleCid || null,
    embedResolvedBy: null,
    embedResolvedMethod: null,
    embedResolveError: null,
  };
}

/** UI helper for embed coordinate lookup feedback. */
export function formatEmbedResolveStatus(resolved) {
  if (resolved?.latitude != null && resolved?.longitude != null) {
    return {
      state: 'ready',
      text: `Location found: ${Number(resolved.latitude).toFixed(5)}, ${Number(resolved.longitude).toFixed(5)}`,
    };
  }
  return {
    state: 'error',
    text: resolved?.embedResolveError
      || 'Could not resolve this embed to a place pin. Check the Google Maps embed and API key settings.',
  };
}

function canUseGoogleMapsJs() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function loadGoogleMapsJs(apiKey) {
  if (!canUseGoogleMapsJs()) {
    return Promise.reject(new Error('Google Maps JS requires a browser'));
  }
  if (googleMapsJsBlocked) {
    return Promise.reject(new Error('ApiNotActivatedMapError'));
  }
  if (window.google?.maps?.importLibrary) {
    return Promise.resolve(window.google.maps);
  }
  if (googleMapsJsPromise && googleMapsJsKey === apiKey) {
    return googleMapsJsPromise;
  }
  googleMapsJsKey = apiKey;
  const priorAuthFailure = window.gm_authFailure;
  window.gm_authFailure = () => {
    googleMapsJsBlocked = true;
    noteGoogleResolveError(new Error('Google Maps authentication failed: the key does not allow this website or API'));
    if (typeof priorAuthFailure === 'function') priorAuthFailure();
  };
  googleMapsJsPromise = new Promise((resolve, reject) => {
    const callbackName = '__vrcGoogleMapsInit';
    window[callbackName] = () => {
      delete window[callbackName];
      if (window.google?.maps) resolve(window.google.maps);
      else reject(new Error('Google Maps JS loaded but maps object missing'));
    };
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&loading=async&callback=${callbackName}`;
    script.async = true;
    script.onerror = () => {
      googleMapsJsPromise = null;
      noteGoogleResolveError(new Error('Failed to load Google Maps JS'));
      reject(new Error('Failed to load Google Maps JS'));
    };
    document.head.appendChild(script);
  });
  return withGoogleTimeout(googleMapsJsPromise, 15000, 'Google Maps script');
}

export const RESOLVE_EMBED_TOTAL_TIMEOUT_MS = 45000;

/** Places API (New) REST call that gives up after `ms`; a stall disables it for the session. */
async function placesFetch(url, init, ms = 10000) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), ms) : null;
  try {
    return await fetch(url, controller ? { ...init, signal: controller.signal } : init);
  } catch (error) {
    if (error?.name === 'AbortError') googlePlacesNewBlocked = true;
    throw error;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

function withGoogleTimeout(promise, ms = 10000, label = 'Google Maps') {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`ApiNotActivatedMapError: ${label} timed out`)), ms);
    }),
  ]);
}

function latLngFromJsLocation(loc) {
  if (!loc) return null;
  const lat = typeof loc.lat === 'function' ? loc.lat() : Number(loc.lat);
  const lng = typeof loc.lng === 'function' ? loc.lng() : Number(loc.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

async function jsGeocodeByAddress(query, googleApiKey) {
  if (!query || !googleApiKey || !canUseGoogleMapsJs() || googleMapsJsBlocked) return null;
  logGoogleCoords('js geocode by address request', { query });
  try {
    const maps = await loadGoogleMapsJs(googleApiKey);
    const { Geocoder } = await withGoogleTimeout(
      maps.importLibrary('geocoding'),
      10000,
      'Geocoder'
    );
    const geocoder = new Geocoder();
    const response = await withGoogleTimeout(geocoder.geocode({ address: query }), 10000, 'Geocoder');
    const results = response?.results || [];
    logGoogleCoords('js geocode by address response', {
      query,
      googleStatus: results.length ? 'OK' : 'ZERO_RESULTS',
      resultCount: results.length,
    });
    if (!results[0]) return null;
    const loc = latLngFromJsLocation(results[0].geometry?.location);
    if (!loc) return null;
    const coords = { ...loc, googlePlaceId: results[0].place_id || null };
    logGoogleCoords('js geocode by address resolved', { query, ...coords });
    return coords;
  } catch (error) {
    noteGoogleResolveError(error);
    logGoogleCoords('js geocode by address threw', {
      query,
      message: error?.message || String(error),
    });
    return null;
  }
}

async function jsGeocodeByPlaceId(placeId, googleApiKey) {
  if (!placeId || !googleApiKey || !canUseGoogleMapsJs() || googleMapsJsBlocked) return null;
  logGoogleCoords('js geocode by place_id request', { placeId });
  try {
    const maps = await loadGoogleMapsJs(googleApiKey);
    const { Geocoder } = await withGoogleTimeout(
      maps.importLibrary('geocoding'),
      10000,
      'Geocoder'
    );
    const geocoder = new Geocoder();
    const response = await withGoogleTimeout(geocoder.geocode({ placeId: String(placeId) }), 10000, 'Geocoder');
    const results = response?.results || [];
    logGoogleCoords('js geocode by place_id response', {
      placeId,
      googleStatus: results.length ? 'OK' : 'ZERO_RESULTS',
      resultCount: results.length,
    });
    if (!results[0]) return null;
    const loc = latLngFromJsLocation(results[0].geometry?.location);
    if (!loc) return null;
    const coords = { ...loc, googlePlaceId: results[0].place_id || placeId };
    logGoogleCoords('js geocode by place_id resolved', coords);
    return coords;
  } catch (error) {
    noteGoogleResolveError(error);
    logGoogleCoords('js geocode by place_id threw', {
      placeId,
      message: error?.message || String(error),
    });
    return null;
  }
}

async function jsLegacyPlaceTextSearch(query, googleApiKey, bias) {
  if (!query || !googleApiKey || !canUseGoogleMapsJs() || googleMapsJsBlocked) return null;
  logGoogleCoords('js legacy textSearch request', {
    query,
    hasBias: bias?.lat != null && bias?.lng != null,
  });
  try {
    const maps = await loadGoogleMapsJs(googleApiKey);
    const div = document.createElement('div');
    const service = new maps.places.PlacesService(div);
    const request = { query };
    if (bias?.lat != null && bias?.lng != null) {
      request.location = new maps.LatLng(bias.lat, bias.lng);
      request.radius = 50000;
    }
    const results = await withGoogleTimeout(new Promise((resolve) => {
      service.textSearch(request, (places, status) => {
        logGoogleCoords('js legacy textSearch response', {
          query,
          status,
          resultCount: Array.isArray(places) ? places.length : 0,
          firstPlaceName: places?.[0]?.name || null,
        });
        resolve(status === maps.places.PlacesServiceStatus.OK ? places : null);
      });
    }), 10000, 'Place text search');
    if (!results?.[0]) return null;
    const loc = latLngFromJsLocation(results[0].geometry?.location);
    if (!loc) return null;
    const coords = { ...loc, googlePlaceId: results[0].place_id || null };
    logGoogleCoords('js legacy textSearch resolved', { query, ...coords });
    return coords;
  } catch (error) {
    noteGoogleResolveError(error);
    logGoogleCoords('js legacy textSearch threw', {
      query,
      message: error?.message || String(error),
    });
    return null;
  }
}

async function jsPlaceSearchByText(query, googleApiKey, bias) {
  if (!query || !googleApiKey || !canUseGoogleMapsJs() || googlePlacesNewBlocked) return null;
  logGoogleCoords('js place searchByText request', {
    query,
    hasBias: bias?.lat != null && bias?.lng != null,
    bias: bias?.lat != null ? { lat: bias.lat, lng: bias.lng } : null,
  });
  try {
    const maps = await loadGoogleMapsJs(googleApiKey);
    const { Place } = await maps.importLibrary('places');
    const request = {
      textQuery: query,
      fields: ['id', 'location', 'displayName'],
    };
    if (bias?.lat != null && bias?.lng != null) {
      request.locationBias = {
        center: { lat: bias.lat, lng: bias.lng },
        radius: 50000,
      };
    }
    const { places } = await withGoogleTimeout(Place.searchByText(request), 10000, 'Place search');
    const place = places?.[0];
    logGoogleCoords('js place searchByText response', {
      query,
      placeCount: Array.isArray(places) ? places.length : 0,
      firstPlaceName: place?.displayName || null,
      firstPlaceId: place?.id || null,
    });
    if (!place?.location) return null;
    const lat = Number(place.location.lat);
    const lng = Number(place.location.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    const coords = {
      lat,
      lng,
      googlePlaceId: place.id ? String(place.id).replace(/^places\//, '') : null,
    };
    logGoogleCoords('js place searchByText resolved', { query, ...coords });
    return coords;
  } catch (error) {
    if (isGoogleJsPermissionDenied(error)) {
      googlePlacesNewBlocked = true;
      logGoogleCoords('places (new) disabled for session — enable Places API (New) or use Geocoder path', {
        query,
        errorMessage: error?.message || String(error),
      });
    } else {
      logGoogleCoords('js place searchByText threw', {
        query,
        message: error?.message || String(error),
      });
    }
    return null;
  }
}

async function jsPlaceDetails(placeId, googleApiKey) {
  if (!placeId || !googleApiKey || !canUseGoogleMapsJs() || googlePlacesNewBlocked) return null;
  const id = String(placeId).replace(/^places\//, '');
  logGoogleCoords('js place details request', { placeId: id });
  try {
    const maps = await loadGoogleMapsJs(googleApiKey);
    const { Place } = await maps.importLibrary('places');
    const place = new Place({ id });
    await withGoogleTimeout(place.fetchFields({ fields: ['id', 'location', 'displayName'] }), 10000, 'Place details');
    logGoogleCoords('js place details response', {
      placeId: id,
      displayName: place.displayName || null,
      hasLocation: Boolean(place.location),
    });
    if (!place.location) return null;
    const lat = Number(place.location.lat);
    const lng = Number(place.location.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    const coords = {
      lat,
      lng,
      googlePlaceId: place.id ? String(place.id).replace(/^places\//, '') : id,
    };
    logGoogleCoords('js place details resolved', coords);
    return coords;
  } catch (error) {
    if (isGoogleJsPermissionDenied(error)) {
      googlePlacesNewBlocked = true;
      logGoogleCoords('places (new) disabled for session — enable Places API (New) or use Geocoder path', {
        placeId: id,
        errorMessage: error?.message || String(error),
      });
    } else {
      logGoogleCoords('js place details threw', {
        placeId: id,
        message: error?.message || String(error),
      });
    }
    return null;
  }
}

/**
 * Pulls the Google place identity out of an embed/share URL.
 * Typical embeds store the feature/CID in `!1s0x…:0x…`, not the pin coordinates.
 * Newer URLs may include an official `place_id` / `ChIJ…` id.
 */
export const extractGooglePlaceRefFromMapEmbed = (mapEmbedLink) => {
  const url = decodeEmbedUrl(mapEmbedLink);
  const result = {
    placeId: null,
    cid: null,
    featureId: null,
    placeName: extractPlaceNameFromMapEmbed(mapEmbedLink),
  };
  if (!url) return result;

  const placeIdParam = url.match(/[?&](?:query_)?place_id=([^&]+)/i);
  if (placeIdParam && placeIdParam[1]) {
    const decoded = decodeURIComponent(placeIdParam[1]);
    if (isOfficialGooglePlaceId(decoded)) result.placeId = decoded;
  }

  const cidParam = url.match(/[?&]cid=(\d+)/i);
  if (cidParam && cidParam[1]) result.cid = cidParam[1];

  const ids = [...url.matchAll(/!1s([^!]+)/g)].map((match) => {
    try {
      return decodeURIComponent(match[1].replace(/\+/g, ' '));
    } catch (e) {
      return match[1];
    }
  });

  ids.forEach((id) => {
    if (isOfficialGooglePlaceId(id)) {
      result.placeId = id;
      return;
    }
    if (/^0x[0-9a-f]+:0x[0-9a-f]+$/i.test(id)) {
      result.featureId = id;
      result.cid = result.cid || cidFromFeatureId(id);
    }
  });

  return result;
};

async function googlePlaceDetailsNew(placeId, googleApiKey) {
  if (!placeId || !googleApiKey || googlePlacesNewBlocked) return null;
  const id = String(placeId).replace(/^places\//, '');
  logGoogleCoords('places details (new) request', { placeId: id });
  try {
    const res = await placesFetch(`https://places.googleapis.com/v1/places/${encodeURIComponent(id)}`, {
      method: 'GET',
      headers: {
        'X-Goog-Api-Key': googleApiKey,
        'X-Goog-FieldMask': 'id,location,displayName',
      },
    });
    const data = await res.json().catch(() => ({}));
    logGoogleCoords('places details (new) response', {
      placeId: id,
      httpStatus: res.status,
      errorStatus: data.error?.status || null,
      errorMessage: data.error?.message || null,
      displayName: data.displayName?.text || null,
      hasLocation: Boolean(data.location),
    });
    if (isGooglePermissionDenied(data, res.status)) {
      googlePlacesNewBlocked = true;
      logGoogleCoords('places details disabled for session (enable Places API New on this key)', {
        errorMessage: data.error?.message || null,
      });
      return null;
    }
    if (!res.ok) return null;
    const coords = coordsFromPlace(data, id);
    if (coords) {
      logGoogleCoords('places details (new) resolved', coords);
    }
    return coords;
  } catch (error) {
    logGoogleCoords('places details (new) threw', {
      placeId: id,
      message: error?.message || String(error),
    });
    return null;
  }
}

async function googleSearchTextCoordinates(query, googleApiKey, bias) {
  if (!query || !googleApiKey || googlePlacesNewBlocked) return null;
  try {
    const body = { textQuery: query };
    if (bias?.lat != null && bias?.lng != null) {
      body.locationBias = {
        circle: {
          center: { latitude: bias.lat, longitude: bias.lng },
          radius: 50000,
        },
      };
    }
    logGoogleCoords('places searchText request', {
      query,
      hasBias: Boolean(body.locationBias),
      bias: body.locationBias?.circle?.center || null,
    });
    const res = await placesFetch('https://places.googleapis.com/v1/places:searchText', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': googleApiKey,
        'X-Goog-FieldMask': 'places.id,places.location,places.displayName',
      },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    const firstPlace = data.places?.[0];
    logGoogleCoords('places searchText response', {
      query,
      httpStatus: res.status,
      errorStatus: data.error?.status || null,
      errorMessage: data.error?.message || null,
      placeCount: Array.isArray(data.places) ? data.places.length : 0,
      firstPlaceName: firstPlace?.displayName?.text || null,
      firstPlaceId: firstPlace?.id || null,
    });
    if (isGooglePermissionDenied(data, res.status)) {
      googlePlacesNewBlocked = true;
      logGoogleCoords('places searchText disabled for session (enable Places API New on this key)', {
        errorMessage: data.error?.message || null,
      });
      return null;
    }
    if (!res.ok) return null;
    const coords = coordsFromPlace(firstPlace, firstPlace?.id);
    if (coords) {
      logGoogleCoords('places searchText resolved', { query, ...coords });
    }
    return coords;
  } catch (error) {
    logGoogleCoords('places searchText threw', {
      query,
      message: error?.message || String(error),
    });
    return null;
  }
}

/**
 * Resolves the actual place pin from a Google Maps embed in the browser.
 */
export const resolveEmbedCoordinatesWithGoogle = async (mapEmbedLink, googleApiKey, options = {}) => {
  const opts = typeof googleApiKey === 'object' && googleApiKey !== null ? googleApiKey : (options || {});
  const saved = await resolveEmbedCoordinatesForSave(mapEmbedLink, googleApiKey, opts);
  return {
    lat: saved.latitude,
    lng: saved.longitude,
    source: saved.embedResolvedBy || null,
    method: saved.embedResolvedMethod || null,
    googlePlaceId: saved.googlePlaceId || null,
    googleCid: saved.googleCid || null,
    featureId: null,
  };
};

/**
 * Resolve embed coords for saving on create/approve. Never persists view-frame numbers.
 * Uses Places API (New) REST where available, then Maps JavaScript API
 * (Geocoder + Place.searchByText) which works with HTTP-referrer browser keys.
 */
export const resolveEmbedCoordinatesForSave = async (mapEmbedLink, googleApiKeyOrOptions = {}, maybeOptions = {}) => {
  const options = (
    googleApiKeyOrOptions && typeof googleApiKeyOrOptions === 'object'
      ? googleApiKeyOrOptions
      : maybeOptions
  ) || {};
  const googleApiKey = resolveGoogleApiKey(googleApiKeyOrOptions);
  resetGoogleResolveSessionErrors();
  const empty = emptyResolveFields();
  if (!mapEmbedLink || !String(mapEmbedLink).trim()) return empty;

  const ref = extractGooglePlaceRefFromMapEmbed(mapEmbedLink);
  const viewCenter = extractCoordinatesFromMapEmbed(mapEmbedLink);
  empty.googlePlaceId = ref.placeId || null;
  empty.googleCid = ref.cid || null;

  logGoogleCoords('start', {
    hasKey: Boolean(googleApiKey),
    keyLength: googleApiKey ? googleApiKey.length : 0,
    placeId: ref.placeId,
    cid: ref.cid,
    featureId: ref.featureId,
    placeName: ref.placeName,
    viewCenter,
    name: options.name || '',
    city: options.city || '',
    state: options.state || '',
  });

  if (!googleApiKey) {
    console.warn('[resolveEmbedCoordinatesForSave] missing REACT_APP_GOOGLE_MAPS_API_KEY (restart web after webpack env change)');
    return {
      ...empty,
      embedResolveError: 'Missing REACT_APP_GOOGLE_MAPS_API_KEY. Restart the web app after updating .env.',
    };
  }

  const placeName = ref.placeName || options.name || '';
  const queries = [...new Set([
    [placeName, options.city, options.state].filter(Boolean).join(', '),
    placeName,
    options.name || '',
  ].filter(Boolean))];

  logGoogleCoords('lookup plan', { queries, hasOfficialPlaceId: Boolean(ref.placeId) });

  const toSave = (coords, method) => {
    const saved = {
      latitude: coords.lat,
      longitude: coords.lng,
      googlePlaceId: coords.googlePlaceId || ref.placeId || null,
      googleCid: ref.cid || null,
      embedResolvedBy: 'google',
      embedResolvedMethod: method,
    };
    logGoogleCoords('resolved', saved);
    return saved;
  };

  // Hard cap on the whole chain so a caller (CSV import, approve) never waits forever.
  const lookup = async () => {
    // Maps JS Geocoder works with HTTP-referrer browser keys (Places API New often does not).
    for (const query of queries) {
      logGoogleCoords('try js_geocode_address', { query });
      const byJsGeocode = await jsGeocodeByAddress(query, googleApiKey);
      if (byJsGeocode) return toSave(byJsGeocode, 'js_geocode_address');
    }

    if (ref.placeId) {
      logGoogleCoords('try js_geocode_place_id', { placeId: ref.placeId });
      const byJsPlaceId = await jsGeocodeByPlaceId(ref.placeId, googleApiKey);
      if (byJsPlaceId) return toSave(byJsPlaceId, 'js_geocode_place_id');
    }

    for (const query of queries) {
      logGoogleCoords('try js_legacy_textSearch', { query });
      const byLegacy = await jsLegacyPlaceTextSearch(query, googleApiKey, viewCenter);
      if (byLegacy) return toSave(byLegacy, 'js_legacy_textSearch');
    }

    if (!googlePlacesNewBlocked && ref.placeId) {
      logGoogleCoords('try places_details', { placeId: ref.placeId });
      const byDetails = await googlePlaceDetailsNew(ref.placeId, googleApiKey);
      if (byDetails) return toSave(byDetails, 'places_details');
      logGoogleCoords('try js_place_details', { placeId: ref.placeId });
      const byJsDetails = await jsPlaceDetails(ref.placeId, googleApiKey);
      if (byJsDetails) return toSave(byJsDetails, 'js_place_details');
    }

    if (!googlePlacesNewBlocked) {
      for (const query of queries) {
        logGoogleCoords('try places_search', { query });
        const bySearch = await googleSearchTextCoordinates(query, googleApiKey, viewCenter);
        if (bySearch) return toSave(bySearch, 'places_search');
      }

      for (const query of queries) {
        logGoogleCoords('try js_place_search', { query });
        const byJsSearch = await jsPlaceSearchByText(query, googleApiKey, viewCenter);
        if (byJsSearch) return toSave(byJsSearch, 'js_place_search');
      }
    }
    return null;
  };

  try {
    const saved = await withGoogleTimeout(lookup(), RESOLVE_EMBED_TOTAL_TIMEOUT_MS, 'Coordinate lookup');
    if (saved) return saved;
  } catch (error) {
    if (/timed out/i.test(String(error?.message || ''))) googlePlacesNewBlocked = true;
    noteGoogleResolveError(error);
    logGoogleCoords('lookup threw', {
      message: error?.message || String(error),
      stack: error?.stack || null,
    });
    return {
      ...empty,
      embedResolveError: lastGoogleResolveError || error?.message || 'Coordinate lookup failed.',
    };
  }

  logGoogleCoords('no coordinates found', { queries, error: lastGoogleResolveError });
  return {
    ...empty,
    embedResolveError: lastGoogleResolveError
      || 'Could not resolve this embed to map coordinates. Enable Maps JavaScript API on your Google key.',
  };
};

/**
 * Coordinate fields to copy onto a published venue. Uses already-saved Google
 * coords when present; otherwise looks up the embed's Google id.
 */
export const attachEmbedCoordinatesToVenuePayload = async (venueLike = {}) => {
  if (
    venueLike.embedResolvedBy === 'google' &&
    venueLike.latitude != null &&
    venueLike.longitude != null
  ) {
    return {
      latitude: venueLike.latitude,
      longitude: venueLike.longitude,
      googlePlaceId: venueLike.googlePlaceId || null,
      googleCid: venueLike.googleCid || null,
      embedResolvedBy: 'google',
      embedResolvedMethod: venueLike.embedResolvedMethod || null,
      embedResolvedAt: venueLike.embedResolvedAt || new Date(),
    };
  }
  if (!venueLike.mapEmbedLink) return {};
  const resolved = await resolveEmbedCoordinatesForSave(venueLike.mapEmbedLink, {
    city: venueLike.city,
    state: venueLike.state,
    name: venueLike.name,
  });
  return {
    ...resolved,
    embedResolvedAt: resolved.latitude != null ? new Date() : null,
  };
};

/**
 * Resolves coordinates from a Google Maps embed using Mapbox Geocoding when the embed
 * only has view center. Use when Google API key is not available.
 * @param {string} mapEmbedLink - The map embed iframe code or URL
 * @param {string} mapboxAccessToken - Mapbox API access token
 * @param {{ city?: string, state?: string }} options - Optional city/state to improve geocode result
 * @returns {Promise<{ lat: number|null, lng: number|null, source: 'mapbox'|'embed' }>}
 */
export const resolveEmbedCoordinatesWithMapbox = async (mapEmbedLink, mapboxAccessToken, options = {}) => {
  const embedCoords = extractCoordinatesFromMapEmbed(mapEmbedLink);
  const placeName = extractPlaceNameFromMapEmbed(mapEmbedLink);

  if (!placeName || !mapboxAccessToken) {
    return { ...embedCoords, source: 'embed' };
  }

  const query = [placeName, options.city, options.state].filter(Boolean).join(', ');
  try {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${mapboxAccessToken}&limit=1&types=address,poi,place`;
    const res = await fetch(url);
    if (!res.ok) return { ...embedCoords, source: 'embed' };
    const data = await res.json();
    if (data.features && data.features.length > 0 && data.features[0].center) {
      const [lng, lat] = data.features[0].center;
      return { lat, lng, source: 'mapbox' };
    }
  } catch (e) {
    console.warn('Mapbox geocode for embed place name failed:', e);
  }
  return { ...embedCoords, source: 'embed' };
};

/**
 * Extracts the address from a Google Maps embed link
 * @param {string} mapEmbedLink - The map embed iframe code
 * @returns {string} - The extracted address
 */
export const extractAddressFromMapEmbed = (mapEmbedLink) => {
  if (!mapEmbedLink) return '';
  
  try {
    // Extract the src URL from the iframe
    const srcMatch = mapEmbedLink.match(/src="([^"]+)"/);
    if (!srcMatch || !srcMatch[1]) return '';
    
    const srcUrl = srcMatch[1];
    const urlParams = new URL(srcUrl).searchParams;
    
    // Try different possible parameters where the address might be stored
    let address = '';
    
    // Check various parameters in order of likelihood
    const paramChecks = ['q', 'place', 'query'];
    for (const param of paramChecks) {
      address = urlParams.get(param);
      if (address) break;
    }
    
    // If still not found, try parsing from the pb parameter
    if (!address) {
      const pbParam = urlParams.get('pb');
      if (pbParam) {
        const markers = pbParam.match(/!3m3!1m2!1s(.*?)!2s(.*?)!/);
        if (markers && markers[2]) {
          address = decodeURIComponent(markers[2].replace(/\+/g, ' '));
        }
      }
    }
    
    if (!address) return '';
    
    // Clean up the address
    return address
      .replace(/\+\d+\.\d+,\s*-?\d+\.\d+/, '')      // Remove coordinates
      .replace(/\+[\w\d]+\+[\w\d]+/, '')            // Remove plus codes
      .replace(/\++/g, ' ')                          // Replace + with spaces
      .replace(/%20/g, ' ')                          // Replace %20 with spaces
      .replace(/\s{2,}/g, ' ')                       // Remove extra spaces
      .replace(/^[0-9]+\s/, '')                      // Remove leading numbers
      .replace(/[!@#$%^&*()<>?\/|}{]/g, '')         // Remove special characters
      .trim();
    
  } catch (error) {
    console.error('Error extracting address:', error);
    return '';
  }
};

/**
 * Normalizes a map embed link to ensure consistent format
 * @param {string} mapEmbedLink - The map embed iframe code or URL
 * @returns {string} - Normalized iframe code
 */
export const normalizeMapEmbed = (mapEmbedLink) => {
  if (!mapEmbedLink) return '';
  
  try {
    // If it's already an iframe, validate and return
    if (mapEmbedLink.includes('<iframe')) {
      return isValidMapEmbed(mapEmbedLink) ? mapEmbedLink : '';
    }
    
    // If it's just a URL, convert to iframe
    if (mapEmbedLink.includes('google.com/maps')) {
      return `<iframe src="${mapEmbedLink}" width="600" height="450" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>`;
    }
    
    return '';
  } catch (error) {
    console.error('Error normalizing map embed:', error);
    return '';
  }
}; 